import { Review, Survey, SurveyAnswer, Tag, TagRelation } from '../db/tables'

export type ReviewSource = 'trustmary' | 'google' | 'facebook' | 'custom' | `${WextractorSource}`

type ReviewSourceDetailRow = {
  name: string
  interpretAs: ReviewSource
}

//This is helper to find source name or another source in which a source should be translated to
export const reviewSourceDetails: Record<ReviewSource, ReviewSourceDetailRow> = {
  trustmary: { name: 'Trustmary', interpretAs: 'trustmary' },
  g2: { name: 'G2', interpretAs: 'g2' },
  capterra: { name: 'Capterra', interpretAs: 'capterra' },
  tripadvisor: { name: 'Tripadvisor', interpretAs: 'tripadvisor' },
  yelp: { name: 'Yelp', interpretAs: 'yelp' },
  google: { name: 'Google', interpretAs: 'google' },
  googlemaps: { name: 'Google', interpretAs: 'google' },
  googleplay: { name: 'Google Play', interpretAs: 'googleplay' },
  facebook: { name: 'Facebook', interpretAs: 'facebook' },
  'facebook-v2': { name: 'Facebook', interpretAs: 'facebook' },
  custom: { name: 'Custom', interpretAs: 'custom' },
}

type WithoutNullProperties<T> = {
  [P in keyof T]: Exclude<T[P], null>
}

export type RequiredNullable<T> = Exclude<T, undefined> | null

export interface UnprocessedSurveyAnswerReviewItem {
  nps: RequiredNullable<SurveyAnswer['data']['nps']>
  name: RequiredNullable<SurveyAnswer['data']['name']>
  feedback: RequiredNullable<SurveyAnswer['data']['feedback']>
  comment: RequiredNullable<SurveyAnswer['data']['comment']>
  commentVideo: RequiredNullable<SurveyAnswer['data']['comment_video']>
  commentThumbnail: RequiredNullable<SurveyAnswer['data']['comment_thumbnail']>
  commentWatermark: RequiredNullable<SurveyAnswer['data']['comment_watermark']>
  imageFilename: RequiredNullable<SurveyAnswer['data']['imageFilename']>
  location: RequiredNullable<SurveyAnswer['data']['location']>
  organization: RequiredNullable<SurveyAnswer['data']['organization']>
  position: RequiredNullable<SurveyAnswer['data']['position']>
  createdAt: SurveyAnswer['created_at']
}

export type ProcessedSurveyAnswerReviewItem = Partial<
  WithoutNullProperties<Omit<UnprocessedSurveyAnswerReviewItem, 'comment'>>
> & {
  type: string
}

export type LegacyReviewItem = Partial<ProcessedSurveyAnswerReviewItem & ReviewItem>

export type ReviewItem = {
  source: Exclude<Review['source'], WextractorSource.GoogleMaps | WextractorSource.Facebook>
  createdAt: Review['created_at']
  personName?: Review['data']['personName']
  personPicture?: Review['data']['personPicture']
  personLocation?: Review['data']['personLocation']
  personOrganization?: Review['data']['personOrganization']
  personTitle?: Review['data']['personTitle']
  commentText?: Review['data']['commentText']
  commentImage?: Review['data']['commentImage']
  //commentGallery?: Review['data']['commentGallery']
  commentVideo?: Review['data']['commentVideo']
  commentVideoThumbnail?: Review['data']['commentVideoThumbnail']
  commentVideoWeb?: Review['data']['commentVideoWeb']
  reviewStarRating?: Review['data']['reviewStarRating']
  reviewNps?: Review['data']['reviewNps']
  importedData?: Review['imported_data']
  contentHTML?: Review['data']['contentHTML']
}

/**
 * Review data types from Wextractor
 */

export enum WextractorSource {
  G2 = 'g2',
  Capterra = 'capterra',
  Tripadvisor = 'tripadvisor',
  Yelp = 'yelp',
  GooglePlay = 'googleplay',
  GoogleMaps = 'googlemaps',
  Facebook = 'facebook-v2',
}

export type WextractorReviewData =
  | G2Review
  | CapterraReview
  | TripadvisorReview
  | YelpReview
  | GooglePlayReview
  | GoogleMapsReview
  | FacebookV2Review

export type WextractorServiceData =
  | G2ReviewData
  | CapterraReviewData
  | TripadvisorReviewData
  | YelpReviewData
  | GooglePlayReviewData
  | GoogleMapsReviewData
  | FacebookV2ReviewData

export type WextractorTotals =
  | G2Totals
  | CapterraTotals
  | TripadvisorTotals
  | YelpTotals
  | GooglePlayTotals
  | GoogleMapsTotals
  | FacebookV2Totals

export interface WextractorReviewBase {
  id: string
  datetime: string
  url: string
  text: string
  reviewer: string
  rating: string | null
}

/**
 * G2
 */

export interface G2Review extends WextractorReviewBase {
  title: string
  reviewer_details: string
  reviewer_avatar: string | null
  rating: string
}

export interface G2Totals {
  review_count: number
  average_rating: string
  ease_of_use_rating?: string
  quality_of_support_rating?: string
  ease_of_setup_rating?: string
  stars_count?: {
    '5_stars': number
    '4_stars': number
    '3_stars': number
    '2_stars': number
    '1_stars': number
  }
}

export interface G2ReviewData {
  reviews: G2Review[]
  totals: G2Totals
}

/**
 * Capterra
 */

export interface CapterraReview extends WextractorReviewBase {
  title: string
  reviewer_avatar: string | null
  pros: string
  cons: string
}

export interface CapterraTotals {
  review_count: number
  overall_rating: string
  ease_of_use_rating: string
  customer_service_rating: string
  functionality_rating: string
  value_for_money_rating: string
  recommendation_rating?: string
}

export interface CapterraReviewData {
  reviews: CapterraReview[]
  totals: CapterraTotals
}

/**
 * Tripadvisor
 */

export interface TripadvisorReview extends WextractorReviewBase {
  reviewer_avatar: string | null
  title: string
  language: string
}

export interface TripadvisorTotals {
  review_count: number
  average_rating: string
}

export interface TripadvisorReviewData {
  reviews: TripadvisorReview[]
  totals: TripadvisorTotals
}

/**
 * Yelp
 */

export interface YelpReview extends WextractorReviewBase {
  avatar: string
  language: string
  reply: { text: string; datetime: string } | null
  rating: string
}

export interface YelpTotals {
  review_count: number // Review count with current search language
  average_rating: string
  languages: {
    language: string
    review_count: number
  }[]
}

export interface YelpReviewData {
  reviews: YelpReview[]
  totals: YelpTotals
}

/**
 * Google Play
 */
export interface GooglePlayReview extends WextractorReviewBase {
  helpful_votes: number
  rating: string
  reply?: {
    text: string
    datetime: string
  }
}

export interface GooglePlayTotals {
  average_rating: string
  review_count: number
  stars_count: {
    '1_stars'?: number
    '2_stars'?: number
    '3_stars'?: number
    '4_stars'?: number
    '5_stars'?: number
  }
}

export interface GooglePlayReviewData {
  reviews: GooglePlayReview[]
  totals: GooglePlayTotals
  next_page_token: string | null
}

/**
 * Google Maps
 */
export interface GoogleMapsReview extends WextractorReviewBase {
  reviewer_avatar: string
  reviewer_id: string
  reviewer_url: string
  rating: string
  language: string
  likes: number
  photos: { url: string }[]
}

export interface GoogleMapsTotals {
  review_count: number
  average_rating: string
  stars_count?: {
    '1_stars': number
    '2_stars': number
    '3_stars': number
    '4_stars': number
    '5_stars': number
  }
}

export interface GoogleMapsReviewData {
  reviews: GoogleMapsReview[]
  place_details: {
    address: string
    name: string
  }
  totals: GoogleMapsTotals
}

/**
 * Facebook (wextractor)
 */
export interface FacebookV2Review extends WextractorReviewBase {
  avatar: string
  reviewer_id: string
  reviewer_url: string
  language: string
  story_id: string
  recommends_rating: string
  images: string[]
}

export interface FacebookV2Totals {
  review_count: number
  average_rating: string
}

export interface FacebookV2ReviewData {
  reviews: FacebookV2Review[]
  totals: FacebookV2Totals
  next_page_cursor: string | null
}

export interface QueryReviewsItem {
  id: Review['id']
  data: Review['data']
  source: Review['source']
  eid: Review['eid']
  published: Review['published']
  published_at: Review['published_at']
  created_at: Review['created_at']
  updated_at: Review['updated_at']
  deleted_at: Review['deleted_at']
  survey_answer_id: SurveyAnswer['id'] | null
  survey_id: Survey['id'] | null
  survey_type: Survey['type'] | null
  survey_name: Survey['name'] | null
  tags: { id: Tag['id']; name: Tag['name']; target_id: TagRelation['target_id'] }[]
  ai_score: Review['ai_score']
}

export interface TrustmaryTotals {
  /**
   * NPS Score calculated from detractors, promoters and passives
   */
  nps: number | null
  /**
   * Number of detractor nps scores (<= 6)
   */
  detractors: number
  /**
   * Number of promoter nps scores (>= 9)
   */
  promoters: number
  /**
   * Number of passive nps scores (7 or 8)
   */
  passives: number
  /**
   * Number of nps scores
   */
  total: number
  /**
   * Number of each star rating that the overall_score is based on
   */
  overall_score_star_counts: { [stars: number]: number }
  /**
   * Number of each nps score that the overall_score is based on
   */
  overall_score_nps_counts: { [nps: number]: number }
  /**
   * Total number of nps and star ratings that the overall_score is based on
   */
  review_count: number
}

export interface ReviewsMeta {
  count: number
  publishedTestimonialCount: number
  stats: {
    overall_score_star_counts: { [stars: number]: number }
    review_count: number
  }
  limit: number
  offset: number
  pages: number
  page: number
}
